// @ts-nocheck
/* eslint-disable */
import { isMapycz, node } from "./utils";
import { getItem } from "~/util/cookies";
import Melown3dPos from "./pos";
import Melown3dBasicCompass from "./basic-compass";
import Melown3dUI from "./ui";
import Melown3dLoader from "./loader";

/**
 * Třída zajišťující zobrazení 3D mapy.
 *
 * @melown - veci od Melownu
 * @signal load nacteni mapy (melown core map-loaded)
 * @signal map-load kompletni nacteni mapy vcetne dat
 * @signal error
 * @signal autorotation-change
 * @signal position-change {Object}
 * @signal yaw-change {Number}
 * @signal fly-start { startPosition, endPosition }
 * @signal fly-progress { position, progress }
 * @signal fly-end { position }
 * @signal m3d-click { x, y, e }
 * @signal m3d-right-click { x, y, e }
 * @signal card-opened
 * @signal card-closed
 * @signal load-done byla zmena v prohlizecce a nacetla se vsechna data
 */
export default class Melown3d {
	constructor(container, opts) {
		this._opts = {
			map: {
				// url spesl, zbytku chybi prefix map[toUpperCase]...
				url: "",
				loadMode: "fit",
				preciseBBoxTest: true,
				preciseDistanceTest: true,
				virtualSurfaces: true,
				texelSizeFit: 1.1,
				texelSizeTolerance: 2.2,
				separateLoader: false,
				heightfiledWhenUnloaded: true, // mrizka
				onCopyright: () => {},
			},
			pos: {
				x: 14.4667,
				y: 50.0833,
				pitch: -90, // deg
				z: 400, // [m]
				fov: 60 // deg - bacha na override z jineho mista
			},
			options: {
				sensitivity: [1, 0.06, 0.05], // sensitivita @melown
				inertia: [0.81, 0.9, 0.7], // setrvacnost @melown
				melownPanSensitivity: 2, // setrvacnost @melown - neni od nich, ale abysme se mu priblizili; zaklad by mel byt 1
				newPanSensitivity: 100,
				minZoom: 180/2, // [m] original 180
				maxZoom: 30000000,
				maxMercZoom: 1, // [merc zoom]
				minPitch: -10, // [deg], puvodne 20
				maxPitch: -90,
				panZoomFactor: 200, // anim + mys; cim mensi, tim vetsi citlivost
				animLength: 166,
				animRotationSpeed: 10,
				animPanSensitivity: 400,
				animYawSensitivity: 100,
				animPitchSensitivity: 50,
				animZoomSensitivity: 2.5,
				flySamplePeriod: 10, // fly - sample duration
				flyMaxDuration: 10000, // fly [ms]
				ophotoDuration: 650,
				defaultElevation: [[0,4000],[1,1]],
				elevationLevel: {
					"19": [[0,4000],[1.2,1.0]],
					"17": [[0,4000],[1.3,1.1]],
					"15": [[0,4000],[1.5,1.3]],
					"11": [[0,4000],[2.0,1.5]],
					"8": [[0,4000],[4.0,3.0]]
				},
				elevationAnimationLength: 300, // ms
				elevationAnimationToleration: 150, // ms vetsinou pulka, zalezi na poctu samplu
				elevationAnimationSample: 10
			},
			conf: {
				noOphotoLoad: false, // zrusit loader s ophotem
				zoomToCoords: true, // zoomovat na souradnice mysi
				melownPan: true, // pouzit plovouci pohyb od melownu? @melown
				melownZoom: false, // pouzit zoom od melownu? Nefunguje pak se zoomToCoords @melown
				compass: Melown3dBasicCompass, // vytvorit kompas - null pokud nechceme, jinak vlastni konstruktor
				texture: null, // trida pro obsluhu textur
				mapset: "zakladni",
				othersLang: `and <a href="#" data-others="1">others</a>`, // a dalsi - preklad pro credits, musi mit odkaz, vevnitr atribut data-others=1
				inFrame: false,
				scene: null,
				noCopyright: false,
				mapserverUrl: "//mapserver.mapy.cz",
				loaderImgCrossOrigin: "anonymous",
			},
			trans: {
				worldSides: "S|SV|V|JV|J|JZ|Z|SZ",
				infoTitle: "Jak ovládnout 3D mapu",
				infoPan: "Stiskem levého tlačítka myši a jejím pohybem se posouváte do stran, dopředu a dozadu.",
				infoZoom: "Otáčením kolečka myši se oddalujete nebo přibližujete.",
				infoPitch: "Stiskem pravého tlačítka myši a jejím pohybem se otáčíte nebo nakláníte mapou.",
				copyrightLicence: `<a href="https://licence.mapy.cz/?doc=mapy_pu" target="_blank" rel="noopener">Podmínky užití</a>`,
			},
		};
		// update confu
		Object.keys(opts).forEach(key => {
			Object.assign(this._opts[key], opts[key]);
		});

		this._const = {
			RETINA_FACTOR: 1.0 / Math.max(1.0, (window.devicePixelRatio || 1) - 1),
			BAD_HEIGHT_MIN: -100,
			BACK_TO_OPHOTO_THRESHOLD: 10,
			MAX_HEIGHT_VIEW_MERC: 5, // max vyska pro vykresleni markeru a car atd.
			MOUSE_ZOOM_DISTANCE: 3, // px pro kazdou stranu, jedna se o vzdalenost mysi od zoomu pres kolecko
			FREE_LAYER_STYLE: {
				layers: {
					lines: {
						line: true,
						"line-width": 14.0,
						"line-color": [255, 0, 0, 200],
						"zbuffer-offset": [0, 0, 0]
					},
					"lines-outline": {
						line: true,
						"line-width": 22.0,
						"line-color": [255, 255, 255, 200],
						"zbuffer-offset": [0, 0, 0]
					}
				}
			},
			FREE_LAYER_HEIGHT_OPTS: {
				HIGH_PRECISION: 62, // hodnota pro pocitani vysek - vyssi presnost
				LOW_PRECISION: 62, // nizsi (jina metoda)
				TIMEOUT: 1500 // timeout pro vyssi preciznost -> pak se pocita mensi, nechceme cekat 10s na vysky
			},
			VIEWS: {
				"zakladni": {
					name: "mapycz-default",
					loaderUrl: `${this._opts.conf.mapserverUrl}/bing/`,
				},
				"zimni": {
					name: "winter",
					loaderUrl: `${this._opts.conf.mapserverUrl}/winter-m/`,
				},
				"turisticka": {
					name: "outdoor",
					loaderUrl: `${this._opts.conf.mapserverUrl}/turist-m/`,
				}
			},
			DEFAULT_VIEW: "zakladni",
			SEZNAM_CREDITS_KEY: "seznamcz",
			SEZNAM_CREDITS_HTML: `&copy; <a href="https://o.seznam.cz/" target="_blank" rel="noopener noreferrer">Seznam.cz, a.s.</a>, ${new Date().getFullYear()}`,
			MELOWN_CREDITS_HTML: `Powered by <a href="https://www.melown.com/products/vts/" target="_blank" rel="noopener noreferrer">VTS 3D Geospatial Software Stack<a/>`
		};

		this._dom = {
			copyright: node("div", { className: "copyright" })
		};
		this._events = [];
		this._container = container;
		this._container.classList.add("melown3d");
		this._container.classList.add("first-load");
		this._ophotoLoader = null;
		this._melownCore = null;
		this._melownMap = null;
		this._melownRender = null;
		this._lastTime = null;
		this._lastPos = null;
		this._layerRedrawLock = false;
		this._autoRotationEnable = false;
		this._preInitCalledQueue = [];
		this._mapyLogo = null;
		this._transform = {};
		this._transformOrder = ["pan", "rotate", "autorotation", "zoom"];
		this._ui = null;
		this._firstLoad = true; // prvni nacteni prohlizecky - downloading === 0
		this._hide3d = true; // skryti canvasu do renderu prvni dlazdice
		this._posChangeConf = {
			id: null,
			timeout: 33
		};
		this._posUpdateConf = {
			id: null,
			timeout: 1000 / 4
		};
		this._trajectory = {
			start: null,
			route: [],
			finished: true,
			samplePeriod: 0,
			fly: false
		};
		this._elev = {
			start: null,
			elevation: [],
			finished: true,
			samplePeriod: 0
		};
		this._layers = [];
		this._freeLayers = [];
		this._freeLayersSave = false;
		this._card = {
			id: null,
			timeout: 300,
			opened: false,
			el: null,
			coverEl: null,
			binds: {
				cardCoverMouseOver: this._cardCoverMouseOver.bind(this),
				cardElMouseOver: this._cardElMouseOver.bind(this),
				removeCard: this.removeCard.bind(this)
			}
		};
		this._clickCoords = null;
		this._zoomData = {
			set: false,
			mx: 0,
			my: 0,
			coords: null
		};
		this._progressData = {
			id: null,
			timeout: 600,
			value: 0
		};
		this._creditsData = {
			id: null,
			timeout: 500,
			lastCont: ""
		};
		this._compass = null;
		this._spinner = null;

		// nove
		this._coordsDeltas = [];
		this._orientationDeltas = [];
		this._viewExtentDeltas = [];

		this._actualMapset = this._opts.conf.mapset;
		this._zoomElevationStep = null;

		let cookie3DSE = getItem("Mapy3DSE");

		if (!cookie3DSE || cookie3DSE == "1") {
			this._activeElevation = true;
		}
		else {
			this._activeElevation = false;
		}

		this._init();
	}

	/**
	 * Destruktor.
	 */
	destroy() {
		this._container.removeEventListener("mouseup", this);
		this._container.removeEventListener("mousedown", this);
		this._container.removeEventListener("mousemove", this);

		if (this._ui) { this._ui.destroy(); }

		if (this._creditsData.id) {
			clearInterval(this._creditsData.id);
		}

		this._creditsData.id = null;
		this._clearTimeoutPosChange();
		this._clearIntervalPosUpdate();
		this._events = [];

		if (this._melownCore) {
			this._layers.forEach(layer => {
				layer.destroy();
			});

			try {
				this._melownCore.destroyMap();
				this._melownCore.destroy();
			}
			catch (err) {
				// eslint-disable-next-line no-console
				console.error(err);
			}

			this._melownCore = null;
			this._melownMap = null;
			this._melownRender = null;
		}

		this._dom.copyright.remove();

		if (this._compass) {
			this._compass.destroy();
		}

		if (this._spinner) {
			this._spinner.remove();
		}
	}

	/**
	 * Vraceni options.
	 *
	 * @return  {Object}
	 */
	get options() {
		return this._opts.options;
	}

	/**
	 * Vraceni konfigurace.
	 *
	 * @return  {Object}
	 */
	get conf() {
		return this._opts.conf;
	}

	get loader() {
		return this._ophotoLoader;
	}

	get texture() {
		return this._opts.conf.texture;
	}

	get ui() {
		return this._ui;
	}

	/**
	 * Přidání posluchače signálu.
	 *
	 * @param {String} name Jméno signálu
	 * @param {Function} cb Callback funkce
	 * @param {Object} [scope] Vlastní scope pro funkci
	 * @chainable
	 */
	addListener(name, cb, scope) {
		if (!name || !cb) return this;

		this._events.push({
			name: name,
			cb: cb,
			scope: scope || this
		});

		return this;
	}

	/**
	 * Odstranění posluchače signálu.
	 *
	 * @param {String} name Jméno signálu
	 * @param {Function} cb Callback funkce
	 * @param {Object} [scope] Vlastní scope pro funkci
	 * @chainable
	 */
	removeListener(name, cb, scope) {
		if (!name) return this;

		let len = this._events.length;

		for (let i = len - 1; i >= 0; i--) {
			let item = this._events[i];

			if (item.name != name) continue;

			if ((!cb || cb == item.cb) && (!scope || scope == item.scope)) {
				this._events.splice(i, 1);
			}
		}
	}

	/**
	 * Vytvor event.
	 *
	 * @param {String} name Jméno eventu
	 * @param {Object} [arg] Volitelný argument
	 */
	_makeEvent(name, arg) {
		if (!name) return;

		this._events.forEach(event => {
			if (event.name == name && typeof event.cb === "function") {
				event.cb.apply(event.scope, [arg]);
			}
		});
	}

	/**
	 * Pridani vrstvy do prohlizecky.
	 *
	 * @param {Melown3dLayer} layer
	 */
	addLayer(layer) {
		if (this._preInitPushCall("_addLayer", arguments)) return;

		this._addLayer(layer);
	}

	/**
	 * Pridani vrstvy z prohlizecky.
	 *
	 * @param {Melown3dLayer} layer
	 */
	removeLayer(layerToRemove) {
		if (this._preInitPushCall("_removeLayer", arguments)) return;

		this._removeLayer(layerToRemove);
	}

	/**
	 * Pridani volne vrstvy - reprezentace trasy.
	 *
	 * @param {String} name ID-nazev vrstvy
	 * @param {Array[Array[x, y]]} lines Body trasy - pole poli
	 * @param  {Object} [layerStyle] Styl pro vykresleni
	 * @param {Array[RGBA]} [layerStyle.lineColor = cervena] Barva cary
	 * @param {Array[RGBA]} [layerStyle.lineWidth = 14] Sirka cary
	 * @param {Array[RGBA]} [layerStyle.lineOffset = [0, 0, 0]] Offset cary
	 * @param {Array[RGBA]} [layerStyle.outlineColor = bila] Barva outline cary
	 * @param {Array[RGBA]} [layerStyle.outlineWidth = 22] Sirka outline cary
	 * @param {Array[RGBA]} [layerStyle.outlineOffset = [0, 0, 0]] Offset outline cary
	 */
	addFreeLayer(name, lines, layerStyle = {}) {
		if (this._preInitPushCall("addFreeLayer", arguments)) return;

		if (!this._melownMap) {
			return;
		}

		let style = JSON.parse(JSON.stringify(this._const.FREE_LAYER_STYLE));
		style.layers.lines["line-color"] = layerStyle.lineColor || style.layers.lines["line-color"];
		style.layers.lines["line-width"] = layerStyle.lineWidth || style.layers.lines["line-width"];
		style.layers.lines["zbuffer-offset"] = layerStyle.lineOffset || style.layers.lines["zbuffer-offset"];

		if (layerStyle.outlineWidth === 0) {
			delete style.layers["lines-outline"];
		}
		else {
			style.layers["lines-outline"]["line-color"] = layerStyle.outlineColor || style.layers["lines-outline"]["line-color"];
			style.layers["lines-outline"]["line-width"] = layerStyle.outlineWidth || style.layers["lines-outline"]["line-width"];
			style.layers["lines-outline"]["zbuffer-offset"] = layerStyle.outlineOffset || style.layers["lines-outline"]["zbuffer-offset"];
		}

		let geodata = this._melownMap.createGeodata();
		lines.forEach(linePoints => {
			let bb = linePoints.map(i => {
				// 0 -> 3, posuneme vysky o chlup 3m nahoru
				return [i[0], i[1], 3];
			});
			geodata.addLineString(bb, "float", null, "line");
		});

		// zrusit callback prvnich vysek?
		let cancel = false;
		// cb - po dokonceni nacteni vysek
		let heightsCb = () => {
			this._freeLayers.push(name);
			this._melownMap.addFreeLayer(name, geodata.makeFreeLayer(style));
			// nastavime view
			let view = this._melownMap.getView();
			view.freeLayers[name] = {};
			this._melownMap.setView(view);
		};

		let id = setTimeout(() => {
			id = null;
			cancel = true;

			// nacteme vysky - mensi presnost
			geodata.processHeights("heightmap-by-precision", this._const.FREE_LAYER_HEIGHT_OPTS.LOW_PRECISION, () => {
				heightsCb();
			});
		}, this._const.FREE_LAYER_HEIGHT_OPTS.TIMEOUT);

		// nacteme vysky - vyssi presnost
		geodata.processHeights("node-by-precision", this._const.FREE_LAYER_HEIGHT_OPTS.HIGH_PRECISION, () => {
			if (id) {
				clearTimeout(id);
				id = null;
			}

			if (cancel) return;

			heightsCb();
		});
	}

	/**
	 * Odebrani volne vrstvy - reprezentace trasy.
	 *
	 * @param {String} name ID-nazev vrstvy
	 */
	removeFreeLayer(name) {
		if (this._preInitPushCall("removeFreeLayer", arguments)) return;

		let ind = this._freeLayers.indexOf(name);

		if (ind != -1) {
			let name = this._freeLayers[ind];
			let view = this._melownMap.getView();

			this._freeLayers.splice(ind, 1);
			delete view.freeLayers[name];
			this._melownMap.removeFreeLayer(name);
			this._melownMap.setView(view);
		}
	}

	/**
	 * Ziskani contentu s 3D.
	 *
	 * @return {Element}
	 */
	getContainer() {
		return this._container;
	}

	/**
	 * Ziskani pozice 3D.
	 *
	 * @return {Object}
	 */
	getPosition() {
		return (this._melownMap ? new Melown3dPos(this._melownMap.getPosition().toArray()) : this._getInitPosition());
	}

	/**
	 * Stred 3d projekce.
	 *
	 * @return {Object}
	 */
	getCenter() {
		let pos = this.getPosition();

		return {
			x: pos.x,
			y: pos.y
		};
	}

	/**
	 * Nastaveni stredu 3d projekce - animovany prechod.
	 *
	 * @param {Number} x Souradnice x
	 * @param {Number} y Souradnice y
	 */
	setCenter(x, y) {
		this.animateTo({
			x: x,
			y: y
		});
	}

	/**
	 * Nastaveni stredu 3d projekce - animovany prechod, vraci promise.
	 * Po 500ms se resolvne promise - neni tam neco jako signal o konci animace.
	 *
	 * @param {Number} x Souradnice x
	 * @param {Number} y Souradnice y
	 * @param {Number} mercZ Novy mercator zoom
	 * @return {JAK.Promise}
	 */
	setCenterZoom(x, y, mercZ) {
		let promise = new JAK.Promise();

		this.animateTo({
			x: x,
			y: y,
			mercZ: Math.max(mercZ + 1, this._opts.options.maxMercZoom)
		});

		// nechame si cas na dokonceni animace
		setTimeout(() => {
			promise.fulfill();
		}, 500);

		return promise;
	}

	/**
	 * Ziskani zoomu 3d projekce v mercatoru.
	 *
	 * @return {Number}
	 */
	getZoom() {
		return this.getPosition().mercZ;
	}

	/**
	 * Ziskani nastaveni max zoomu 3d projekce v mercatoru.
	 *
	 * @return {Number}
	 */
	getMaxZoom() {
		return this._opts.options.maxMercZoom;
	}

	/**
	 * Prevod pixelu na souradnice.
	 *
	 * @param  {Number} x Pixelova souradnice x
	 * @param  {Number} y Pixelova souradnice y
	 * @param  {String} [mode = "fix"] Dalsi volitelny float
	 * @return {Object}
	 */
	pixelToCoords(x, y, mode = "fix") {
		let coords = this._getHitCoords(x, y, mode);

		return (coords ? {
			x: coords[0],
			y: coords[1]
		} : null);
	}

	/**
	 * Prevod souradnic na pixel.
	 *
	 * @param  {Number} x Souradnice x
	 * @param  {Number} y Souradnice y
	 * @param  {String} [mode = "fix"] Dalsi volitelny float
	 * @return {Object}
	 */
	coordsToPixel(x, y, mode = "fix") {
		let pixel = this._melownMap ? this._melownMap.convertCoordsFromNavToCanvas([x, y, 0], mode) : null;

		return (pixel ? {
			x: pixel[0],
			y: pixel[1]
		} : null);
	}

	/**
	 * Zamknuti prekresleni vlastnich vrstev.
	 */
	lock() {
		if (!this._layerRedrawLock) {
			this._layerRedrawLock = true;
		}
	}

	/**
	 * Odemknuti prekresleni vlastnich vrstev.
	 */
	unlock() {
		if (this._layerRedrawLock) {
			this._layerRedrawLock = false;
			this.redraw();
		}
	}

	/**
	 * Prekresleni contentu
	 */
	redraw() {
		if (this._melownMap) {
			this._melownMap.redraw();
		}
	}

	/**
	 * Nastav pozici do 3D.
	 *
	 * @param {Object} opts Povinna konfigurace, klice [x, y, z, fov, ...]
	 */
	setPosition(opts) {
		if (this._preInitPushCall("setPosition", arguments)) return;

		if (typeof opts !== "object" || !opts) return;

		// update vadstena position
		this._setPosition(this.getPosition().setData(opts));
	}

	/**
	 * Povolit/zakazat 3D.
	 *
	 * @param {Boolean} enabled
	 */
	setUIEnabled(enabled) {
		if (this._preInitPushCall("setUIEnabled", arguments)) {
		} else {
			if (enabled && !this._ui) {
				this._ui = new Melown3dUI(this, {
					inFrame: this._opts.conf.inFrame,
					trans: this._opts.trans,
				});
				this._ui.activeElevation(true);
				this._ui.activeElevation(this._activeElevation);
				this._disableElevation(this._actualMapset == "letecka");
				let btn = this._container.querySelector(".ui-controls button.elevation span");
				if (btn) {
					btn.innerHTML = this._elev.elevation;
				}
			}
			else if (!enabled && this._ui) {
				this._ui.destroy();
				this._ui = null;
			}
		}
	}

	/**
	 * Je povolena autorotace?
	 *
	 * @return {Boolean}
	 */
	isAutoRotationEnabled() {
		return this._autoRotationEnable;
	}

	/**
	 * Nastavit autorotaci. Pokud neni zadan stav, funguje jako toggle.
	 *
	 * @param {Boolean} [enable] Vlastni stav
	 */
	setAutoRotation(enable) {
		let current = this._autoRotationEnable;
		let newState = enable;

		if (typeof newState === "boolean") {
			this._autoRotationEnable = !!newState;
		}
		else {
			// toggle
			newState = !current;
		}

		this._zoomData.set = false;
		this._autoRotationEnable = newState;

		if (current != this._autoRotationEnable) {
			this._makeEvent("autorotation-change");
		}

		// smazeme predesly interval
		this._clearIntervalPosUpdate();

		// add autorotation
		if (newState) {
			this._pushTransfOper("autorotation", this._opts.options.animRotationSpeed);
			this._runIntervalPosUpdate();
		}
		else {
			this._removeTransfOper("autorotation");
		}
	}

	/**
	 * Zakladni operace nad 3D - pohyb (pan).
	 *
	 * @melown
	 * @param {Number} x pohyb v ose
	 * @param {Number} y pohyb v ose
	 * @param {Boolean} [anim = false] animovany pohyb
	 * @return {Boolean} vraci false pri chybe
	 */
	pan(x, y, anim) {
		if (!this._melownMap) return false;

		this._zoomData.set = false;

		let pos = this._melownMap.getPosition();

		if (pos.getHeightMode() == 'fix') {
			let pos2 = this._melownMap.convertPositionHeightMode(pos, 'float', true);
			if (pos2 != null) {
				pos = pos2;
				this._melownMap.setPosition(pos);
			}
		}

		if (this._opts.conf.melownPan) {
			x *= this._opts.options.melownPanSensitivity;
			y *= this._opts.options.melownPanSensitivity;

			let sensitivity = this._opts.options.sensitivity[0] * this._const.RETINA_FACTOR;
			let pos = this.getPosition();
			let fovCorrection = (pos.fov > 0.01 && pos.fov < 179) ? (1.0 / Math.tan(vts.math.radians(pos.fov * 0.5))) : 1.0;
			let azimuthDistance = this._getAzimuthAndDistance(x, y);
			let azimuth = vts.math.radians(azimuthDistance[0]);
			let forward = [
				Math.sin(azimuth), //direction vector x
				Math.cos(azimuth), //direction vector y
				azimuthDistance[1] * fovCorrection * sensitivity, azimuthDistance[0], //distance and azimut
				pos.x,
				pos.y //coords
			];

			this._coordsDeltas.push(forward);
			this._reduceFloatingHeight(0.8);

			// nastavime novou pozici -> doslo ke zmene, nastavime
			if (this._updateDeltas(pos, true)) {
				this._setPosition(pos);
			}
		}
		else {
			if (anim) {
				x *= this._opts.options.animPanSensitivity;
				y *= this._opts.options.animPanSensitivity;

				this._pushTransfOper("pan", x, y);
			}
			else {
				this._setPosition(this._applyTransf(this.getPosition(), "pan", x, y));
			}
		}

		this._reduceFloatingHeight(0.9);
	}

	/**
	 * Zakladni operace nad 3D - rotace (rotate).
	 *
	 * @param {Number} x rotace yaw
	 * @param {Number} y rotace pitch
	 * @param {Boolean} [anim = false] animovany pohyb
	 * @return {Boolean} vraci false pri chybe
	 */
	rotate(x, y, anim) {
		if (!this._melownMap) return false;

		this._zoomData.set = false;

		if (anim) {
			x *= this._opts.options.animYawSensitivity;
			y *= this._opts.options.animPitchSensitivity;

			this._pushTransfOper("rotate", x, y);
		}
		else {
			this._setPosition(this._applyTransf(this.getPosition(), "rotate", x, y));
		}
	}

	/**
	 * Zakladni operace nad 3D - zoom
	 *
	 * @param {Number} x hodnota zoomu
	 * @return {Boolean} vraci false pri chybe
	 */
	zoom(x) {
		if (!this._melownMap) return false;

		this._setPosition(this._applyTransf(this.getPosition(), "zoom", x));

		this._reduceFloatingHeight(0.9);
	}

	/**
	 * Zoom na souradnice. Stred cajk, jinak se zoomuje na stred pozice mysi a stredu obrazovky.
	 *
	 * @param  {Number} zoomDir Smer zoomu - 1 dolu, -1 nahoru
	 * @param  {Number} [dx] 0..n sirka viewportu
	 * @param  {Number} [dy] 0..n vyska viewportu
	 */
	zoomToCoords(zoomDir, dx, dy) {
		if (!this._melownMap) return false;

		let pos = this.getPosition();

		// zoom podle melownu
		if (this._opts.conf.melownZoom) {
			let sensitivity = this._opts.options.sensitivity[2];
			let factor = 1.0 + zoomDir * sensitivity;

			this._viewExtentDeltas.push(factor);
			this._reduceFloatingHeight(0.8);

			this._updateDeltas(pos, false, false, true);
		}
		else {
			// nas zoom + moznost zoomu na pozici mysi
			let newX = pos.x;
			let newY = pos.y;

			if (this._opts.conf.zoomToCoords && typeof dx === "number" && typeof dy === "number") {
				let coords = null;

				if (this._zoomData.set && Math.sqrt(Math.pow(dx - this._zoomData.mx, 2) + Math.pow(dy - this._zoomData.my, 2)) <= this._const.MOUSE_ZOOM_DISTANCE) {
					coords = this._zoomData.coords;
				}
				else {
					coords = this._getHitCoords(dx, dy);

					// bugfix - na cerne dlazdici to nesedi
					if (!coords) {
						coords = null;
						this._zoomData.set = false;
					}
					else {
						this._zoomData.set = true;
						this._zoomData.mx = dx;
						this._zoomData.my = dy;
						this._zoomData.coords = coords;
					}
				}

				// zazumujeme
				if (coords) {
					newX = (coords[0] - pos.x) * 0.5 * -zoomDir + pos.x;
					newY = (coords[1] - pos.y) * 0.5 * -zoomDir + pos.y;
				}
			}

			this.animateTo({
				x: newX,
				y: newY,
				z: pos.z * (zoomDir > 0 ? 1.5 : 0.5)
			}, {
					steps: 25,
					stopMoveOnBottom: true
				});
		}

		this._reduceFloatingHeight(0.8);
	}

	/**
	 * Letecky mod na vybranou pozici.
	 *
	 * @param {Object} konfigurace pro pozici {x, y, yaw...}
	 * @return {Boolean} vraci false pri chybe
	 */
	flyTo(posObj) {
		if (!posObj || !this._melownMap) return false;

		let trajectory = this._melownMap.generateTrajectory(this.getPosition().toArray(), this.getPosition().setData(posObj).toArray(), {
			samplePeriod: this._opts.options.flySamplePeriod,
			maxDuration: this._opts.options.flyMaxDuration
		});

		this._setTrajectory(trajectory, {
			flyMode: true,
			samplePeriod: this._opts.options.flySamplePeriod
		});
	}

	/**
	 * Zruseni letu.
	 */
	cancelFlight() {
		if (!this._trajectory.finished && this._trajectory.flyMode) {
			this._trajectory.route = [];
			this._trajectory.finished = true;
			this._trajectory.flyMode = false;
		}
	}

	/**
	 * Animovany pohyb na novou pozici definovanou objektem.
	 * Pohyb je primocary po primce (vlastni implementace).
	 *
	 * @param  {Object} posObj Do jake pozice animovat
	 * @param {Object} [opts] Volitelna konfigurace
	 * @param  {Number} [opts.steps = 10] Pocet kroku - klik kroku vygenerovat
	 * @param  {Number} [opts.samplePeriod = 10] Samplovaci perioda
	 * @param  {Boolean} [opts.stopMoveOnBottom = false] Pri dosazeni spodni casti zoomu (zeme) zastavit generovani trajektorie
	 */
	animateTo(posObj, opts) {
		if (!posObj) return false;

		let params = Object.assign({
			steps: 10,
			samplePeriod: 10,
			stopMoveOnBottom: false
		}, opts);

		let trajectory = this._generateTrajectory(posObj, params.steps, params.stopMoveOnBottom);

		this._setTrajectory(trajectory, {
			samplePeriod: params.samplePeriod
		});
	}

	elevation(activate) {
		this._activeElevation = activate;
		this._elevAnime(activate);
	}

	/**
	 * Animovany prechod - nastavi novy pohled pro 3d, ktery by mel splyvat s prechodem na ophoto.
	 *
	 * @return {JAK.Promise}
	 */
	backToOphoto() {
		let promise = new JAK.Promise();
		let pos = this.getPosition();

		// ma cenu animovat? - moc jsme se ve 3d nehybali, takze neanimujeme
		if ((this._isInInterval(pos.yaw, -this._const.BACK_TO_OPHOTO_THRESHOLD, 0) || this._isInInterval(pos.yaw, this._const.BACK_TO_OPHOTO_THRESHOLD, 0)) &&
			this._isInInterval(pos.pitch, this._opts.options.maxPitch + this._const.BACK_TO_OPHOTO_THRESHOLD, this._opts.options.maxPitch)) {
			setTimeout(() => {
				promise.fulfill(this.getPosition());
			}, 100);

			return promise;
		}

		this.flyTo({
			yaw: 0,
			pitch: -90
		});

		setTimeout(() => {
			promise.fulfill(this.getPosition());
		}, this._opts.options.ophotoDuration + this._opts.options.ophotoDuration * 0.5);

		return promise;
	}

	/**
	 * Nastaveni pozice prohlizecky na sever.
	 */
	setToNorth() {
		let pos = this.getPosition();

		// ma cenu animovat? - moc jsme se ve 3d nehybali, takze neanimujeme
		if (this._isInInterval(pos.yaw, -this._const.BACK_TO_OPHOTO_THRESHOLD, 0) || this._isInInterval(pos.yaw, this._const.BACK_TO_OPHOTO_THRESHOLD, 0)) {
			this.setPosition({
				yaw: 0
			});
		}
		else {
			this.flyTo({
				yaw: 0
			});
		}
	}

	/**
	 * Je otevreny ui dialog?
	 *
	 * @return {Boolean}
	 */
	isDialogOpen() {
		return (this._ui ? this._ui.isDialogOpen() : false);
	}

	/**
	 * Pridani vizitky do 3d. Vytvori se nad canvasem div, do ktereho se vizitka vlozi.
	 *
	 * @param {Element} el Karta
	 * @param {Boolean} [disableEvents = false] Zrusit pridani eventu a vytvoreni eventu card-opened
	 * @return {Element} Obalovaci element - cover
	 */
	addCard(el, disableEvents) {
		this.removeCard();

		let cover = document.createElement("div");
		cover.classList.add("card-cover");
		cover.appendChild(el);

		this._container.appendChild(cover);
		this._container.classList.remove("cur-pointer");

		this._card.opened = true;
		this._card.el = el;
		this._card.cover = cover;

		// eventy
		if (!disableEvents) {
			this._card.el.addEventListener("mouseover", this._card.binds.cardElMouseOver);
			this._card.cover.addEventListener("mouseover", this._card.binds.cardCoverMouseOver);
			document.addEventListener("mouseover", this._card.binds.removeCard);

			this._makeEvent("card-opened");
		}

		return cover;
	}

	/**
	 * Odebrani karty.
	 *
	 * @param {Boolean} [disableEvents = false] Zrusit odebrani eventu a vytvoreni eventu card-closed
	 */
	removeCard(disableEvents) {
		this._clearCardTimeout();

		if (this._card.opened) {
			// eventy
			if (!disableEvents) {
				this._card.el.removeEventListener("mouseover", this._card.binds.cardElMouseOver);
				this._card.cover.removeEventListener("mouseover", this._card.binds.cardCoverMouseOver);
				document.removeEventListener("mouseover", this._card.binds.removeCard);
			}

			this._card.cover.remove();

			this._card.opened = false;
			this._card.el = null;
			this._card.cover = null;

			if (!disableEvents) {
				this._makeEvent("card-closed");
			}
		}
	}

	/**
	 * Je karta pripnuta?
	 *
	 * @return {Boolean}
	 */
	isCardOpen() {
		return this._card.opened;
	}

	/**
	 * Handlovani kliknuti ve 3d a pohybu mysi.
	 * @param  {Event} e
	 */
	handleEvent(e) {
		if (!this._melownCore) return;

		switch (e.type) {
			case "mousedown":
				if (e.target.nodeName.toLowerCase() == "canvas") {
					this._clickCoords = {
						x: e.clientX,
						y: e.clientY
					};
				}
				else {
					this._clickCoords = null;

					e.stopPropagation();
					e.preventDefault();
				}
				break;

			case "mouseup":
				if (this._clickCoords && this._clickCoords.x == e.clientX && this._clickCoords.y == e.clientY) {
					let leftClick = e.which == 1 || e.button == 1;
					let rightClick = e.which == 3 || e.button == 3;

					// nejdrive test na prave -> pak se nespousti layers event
					if (rightClick || (leftClick && !this._layersEvent("click", e))) {
						let hitCoords = this._getHitCoords(e.clientX, e.clientY);

						if (!hitCoords) {
							return;
						}

						this._makeEvent(leftClick ? "m3d-click" : "m3d-right-click", {
							x: hitCoords[0],
							y: hitCoords[1],
							e: e
						});

						// pri pravem tlacitku prekreslime
						if (rightClick) {
							this.redraw();
						}
					}
				}

				this._clickCoords = null;
				break;

			case "mousemove":
				this._clickCoords = null;

				this._layersEvent("move", e);
				break;
		}
	}

	/**
	 * Velikost 3d viewportu.
	 *
	 * @return {Object}
	 */
	getSize() {
		let width = 0;
		let height = 0;

		if (this._melownRender) {
			let size = this._melownRender.getCanvasSize();

			width = size[0];
			height = size[1];
		}

		return {
			x: width,
			y: height
		};
	}

	/**
	 * Vytvoreni obrazku se screenshotem 3d.
	 *
	 * @param  {Boolean} [addLogo = true] přidávat logo
	 * @return {Object} src, width, height
	 */
	getScreenshot(addLogo = true) {
		if (this._melownMap) {
			let renderData = this._melownMap.renderToImage();
			let output = {
				width: renderData.width,
				height: renderData.height
			};
			let canvas = document.createElement('canvas');
			let ctx = canvas.getContext('2d');
			canvas.width = renderData.width;
			canvas.height = renderData.height;
			let imageData = ctx.createImageData(renderData.width, renderData.height);
			for (let i = 0; i < renderData.data.length; i++) {
				imageData.data[i] = renderData.data[i];
			}
			ctx.putImageData(imageData, 0, 0);

			// logo do praveho spodniho rohu
			if (addLogo && this._mapyLogo) {
				let bottom = 10;
				let right = 10;

				ctx.drawImage(this._mapyLogo, output.width - right - this._mapyLogo.width, output.height - bottom - this._mapyLogo.height, this._mapyLogo.width, this._mapyLogo.height);
			}
			output.src = canvas.toDataURL();
			renderData = null;
			imageData = null;

			return output;
		}
		else {
			return null;
		}
	}

	/**
	 * Nastaveni mapsetu pro 3d.
	 *
	 * @param   {String}  mapset Nazev
	 */
	setMapset(mapset) {
		if (this._melownMap) {
			let pos = this._melownMap.getPosition();

			if (pos.getHeightMode() == 'float') {
				let pos2 = this._melownMap.convertPositionHeightMode(pos, 'fix', true);
				if (pos2 != null) {
					pos = pos2;
					this._melownMap.setPosition(pos);
				}
			}

			this._elevationAnimation(mapset).then(afterAnimation => {
				// pokud jsme meli vlastni vrstvu, tak si to zapamatujeme
				this._melownMap.setView(this._const.VIEWS[this._get3dMapset(mapset)].name);
				let view = this._melownMap.getView();
				this._freeLayers.forEach(layerName => {
					view.freeLayers[layerName] = {};
				});
				this._melownMap.setView(view);

				if (afterAnimation) {
					this._elevationAnimation(mapset, true);
				}

				this._actualMapset = mapset;

				this._disableElevation(mapset == "letecka");
			}, e => {});
		}
	}

	/**
	 * Ziskani mapsetu pro 3d.
	 *
	 * @param   {String}  mapset Nazev
	 * @return  {String}
	 */
	_get3dMapset(mapset) {
		if (Object.keys(this._const.VIEWS).indexOf(mapset) == -1) {
			mapset = this._const.DEFAULT_VIEW;
		}

		return mapset;
	}

	/**
	 * Vytvor 3d config.
	 * @return {Object}
	 */
	_getCoreConf() {
		// conf
		let melownConf = {};

		Object.keys(this._opts.map).forEach(key => {
			let paramName;

			switch (key) {
				case "url":
					paramName = "map";
					break;

				default:
					// add map + camel case
					paramName = "map" + key[0].toUpperCase() + key.substr(1);
			}

			melownConf[paramName] = this._opts.map[key];
		});

		// position - tady musi byt array
		melownConf.position = this._getInitPosition().toArray();
		melownConf.view = this._const.VIEWS[this._get3dMapset(this._opts.conf.mapset)].name;

		return melownConf;
	}

	/**
	 * 3D init
	 */
	_init() {
		if (!vts.checkSupport()) {
			// asynchronne zavolame chybu
			setTimeout(() => {
				this._makeEvent("error", { message: "Not supported" });
			}, 100);

			return;
		}

		// hlavni objekt 3D
		this._melownCore = vts.core(this._container, this._getCoreConf());

		this._melownCore.on("renderer-shader-error", () => {
			this._makeEvent("error", { message: "Shader error" });
		});

		// ztrata contextu
		this._melownCore.on("gpu-context-lost", () => {
			let reloadM3D = () => {
				this._opts.conf.scene?.reload();

				window.removeEventListener("focus", reloadM3D);
			};

			window.addEventListener("focus", reloadM3D);
		});

		this._melownCore.on("map-loaded", () => {
			// variables
			this._melownMap = this._melownCore.map;
			this._melownRender = this._melownCore.renderer;

			this._countElevationZoomStep();

			this._setZoomElevation(this.getPosition().mercZ-1);

			if (this._opts.conf.texture) {
				this._opts.conf.texture.render = this._melownRender;
			}

			// layers render
			this._melownMap.addRenderSlot("custom-render", () => {
				this._customRender();
			}, true);

			this._melownMap.moveRenderSlotAfter("after-map-render", "custom-render");

			// copyright el
			!this._opts.conf.noCopyright && this._container.appendChild(this._dom.copyright);

			// copyright refresh
			this._creditsData.id = setInterval(() => {
				this._setCopyright();
			}, this._creditsData.timeout);

			// 3d is load
			this._makeEvent("load");
		});

		// map change
		this._melownCore.on("map-position-changed", pos => {
			// nefunguje unbind
			if (!this._melownMap) return;

			// pos change event
			this._posChange(new Melown3dPos(pos.position));
		});

		// map tick
		this._melownCore.on("tick", () => {
			this._tick();
		});

		// loader
		this._spinner = document.createElement("div");
		this._spinner.classList.add("spinner");

		for (let i = 1; i < 6; i++) {
			let spinnerItem = document.createElement("div");
			spinnerItem.classList.add("rect" + i);

			this._spinner.appendChild(spinnerItem);
		}

		this._container.appendChild(this._spinner);

		// eventy
		this._container.addEventListener("mouseup", this);
		this._container.addEventListener("mousedown", this);
		this._container.addEventListener("mousemove", this);

		// ophoto loader
		if (!this._opts.conf.noOphotoLoad) {
			let width = this._container.offsetWidth;
			let height = this._container.offsetHeight;
			let pos = this._getInitPosition();

			this._ophotoLoader = new Melown3dLoader(width, height, pos.x, pos.y, pos.mercZmap, this._const.VIEWS[this._get3dMapset(this._opts.conf.mapset)].loaderUrl, this._opts.conf.loaderImgCrossOrigin);
			this._ophotoLoader.classList.add("ophoto-loader");
			this._container.insertBefore(this._ophotoLoader, this._container.firstChild);
		}

		// logo
		if (isMapycz()) {
			const logoImage = new Image();

			logoImage.crossOrigin = "anonymous";
			logoImage.addEventListener("load", () => {
				this._mapyLogo = logoImage;
			});
			logoImage.src = `/img/logo/com/logo-print.svg`;
		}
	}

	/**
	 * Uprava vysky nad terenem.
	 *
	 * @melown
	 * @param {Float}  factor Hodnota factoru
	 */
	_reduceFloatingHeight(factor) {
		if (this._melownMap) {
			let pos = this._melownMap.getPosition();
			let coords = pos.getCoords();

			if (pos.getHeightMode() == 'float' &&
				pos.getViewMode() == 'obj') {
				if (coords[2] != 0) {
					coords[2] *= factor;

					if (Math.abs(coords[2]) < 0.1) {
						coords[2] = 0;
					}

					pos.setCoords(coords);
					this._melownMap.setPosition(pos);
				}
			}
		}
	}

	/**
	 * Spocitani azimutu a vzdalenosti.
	 *
	 * @melown
	 * @param   {Number}  dx  Delta X
	 * @param   {Number}  dy  Delta Y
	 * @return  {Array}
	 */
	_getAzimuthAndDistance(dx, dy) {
		let pos = this.getPosition();
		let fov = pos.fov * 0.5;
		let zoomFactor = (((pos.z * 0.5) * Math.tan(vts.math.radians(fov))) / 800);
		dx *= zoomFactor;
		dy *= zoomFactor;

		let distance = Math.sqrt(dx * dx + dy * dy);
		let azimuth = -vts.math.degrees(Math.atan2(dx, dy)) + pos.yaw;

		return [azimuth, distance];
	}

	/**
	 * Update dat.
	 *
	 * @melown
	 * @param   {Melown3dPos}  pos Vstupni pozice
	 * @param   {Boolean}  onlyLastPan Pohyb
	 * @param   {Boolean}  onlyLastRotate Rotace
	 * @param   {Boolean}  onlyLastZoom Zoom
	 * @return {Boolean} Doslo ke zmene
	 */
	_updateDeltas(pos, onlyLastPan, onlyLastRotate, onlyLastZoom) {
		let change = false;

		if (!onlyLastRotate && !onlyLastZoom && this._coordsDeltas.length > 0) {
			let deltas = this._coordsDeltas;
			let forward = [0, 0];
			let azimuth;

			//get foward vector form coord deltas
			for (let i = (onlyLastPan ? (deltas.length - 1) : 0); i < deltas.length; i++) {
				let delta = deltas[i];

				azimuth = delta[3];
				azimuth = vts.math.radians(azimuth);

				forward[0] += Math.sin(azimuth) * delta[2];
				forward[1] += Math.cos(azimuth) * delta[2];

				delta[2] *= this._opts.options.inertia[0];

				//remove zero deltas
				if (delta[2] < 0.01) {
					deltas.splice(i, 1);
					i--;
				}
			}

			let distance = Math.sqrt(forward[0] * forward[0] + forward[1] * forward[1]);
			azimuth = vts.math.degrees(Math.atan2(forward[0], forward[1]));

			//apply final azimuth and distance
			let correction = pos.yaw;
			pos.setData(this._melownMap.movePositionCoordsTo(pos.toArray(), azimuth, distance, 1).toArray());
			correction = pos.yaw - correction;

			for (let i = 0; i < deltas.length; i++) {
				let delta = deltas[i];
				delta[3] += correction;
			}

			change = true;
		}

		//process coords deltas
		if (!onlyLastPan && !onlyLastZoom && this._orientationDeltas.length > 0) {
			let deltas = this._orientationDeltas;
			let yaw = pos.yaw;
			let pitch = pos.pitch;
			let roll = pos.roll;

			//apply detals to current orientation
			for (let i = (onlyLastRotate ? (deltas.length - 1) : 0); i < deltas.length; i++) {
				let delta = deltas[i];

				yaw += delta[0];
				pitch += delta[1];
				roll += delta[2];
				delta[0] *= this._opts.options.inertia[1];
				delta[1] *= this._opts.options.inertia[1];
				delta[2] *= this._opts.options.inertia[1];

				//remove zero deltas
				if (delta[0] * delta[0] + delta[1] * delta[1] + delta[2] * delta[2] < 0.1) {
					deltas.splice(i, 1);
					i--;
				}
			}

			//apply final orintation
			// HACK
			pos.yaw = yaw;
			pos.pitch = pitch;
			pos.roll = roll;
			change = true;
		}

		//process view extents deltas
		if (!onlyLastRotate && !onlyLastPan && this._viewExtentDeltas.length > 0) {
			let deltas = this._viewExtentDeltas;
			let z = pos.z;

			//apply detals to current view extent
			for (let i = (onlyLastZoom ? (deltas.length - 1) : 0); i < deltas.length; i++) {
				z *= deltas[i];
				deltas[i] += (1 - deltas[i]) * (1.0 - this._opts.options.inertia[2]);

				//remove zero deltas
				if (Math.abs(1 - deltas[i]) < 0.001) {
					deltas.splice(i, 1);
					i--;
				}
			}

			//apply final view extrent
			pos.z = Math.max(1, z);
			change = true;
		}

		return change;
	}

	/**
	 * Map tick.
	 */
	_tick() {
		if (!this._melownMap) return;

		let now = Date.now();
		let pos = this.getPosition();
		let posChange = false;
		let deltaTimeSeconds = (now - this._lastTime) / 1000;

		// trajektorie
		if (!this._trajectory.finished && this._trajectory.route.length) {
			let route = this._trajectory.route;
			let diff = now - this._trajectory.start;
			let sampleIndex = Math.floor(diff / this._trajectory.samplePeriod);
			let totalSamples = route.length - 1;

			if (sampleIndex < totalSamples) {
				//interpolace - nastavime pozici data z trajektorie
				pos.setData(route[sampleIndex]);
				posChange = true;

				if (this._trajectory.flyMode) {
					this._makeEvent("fly-progress", {
						position: route[sampleIndex],
						progress: Math.round(100 * sampleIndex / totalSamples)
					});
				}
			}
			else {
				pos.setData(route[totalSamples]);
				posChange = true;
			}

			if (sampleIndex >= totalSamples) {
				// clear interval
				this._clearIntervalPosUpdate();

				let wasFlyMode = this._trajectory.flyMode;

				// reset
				this._trajectory.finished = true;
				this._trajectory.route = [];
				this._trajectory.flyMode = false;
				this._trajectory.samplePeriod = 0;

				if (wasFlyMode) {
					this._makeEvent("fly-end", {
						position: route[totalSamples]
					});
				}
			}
		}

		if (this._elev.elevation.length) {
			this._elevation(this._elev.elevation);

			if (this._elev.finished) {
				this._elev.elevation = [];
			}
		}

		// transformace podle poradi
		this._transformOrder.forEach(transName => {
			if (!(transName in this._transform)) return;

			let transItem = this._transform[transName];

			if (now > transItem.end && !(this._autoRotationEnable && transName == "autorotation" && !this._hide3d)) {
				return;
			}

			// update pozice
			pos = this._applyTransf(pos, transName, transItem.x * deltaTimeSeconds, transItem.y * deltaTimeSeconds, transItem.z * deltaTimeSeconds);
			posChange = true;
		});

		// pohyb @melown
		if (this._updateDeltas(pos)) {
			posChange = true;
		}

		// pri zmene pozice - update mapy
		if (posChange) {
			this._setPosition(pos);
		}

		// nacitani dat
		let stats = this._melownMap.getStats();

		if (this._progressData.value > 0 && stats.downloading === 0) {
			this._progressData.value = 0;

			this._progressData.id = setTimeout(() => {
				this._progressData.id = null;

				this._dataLoadDone();
			}, this._progressData.timeout);
		}
		else if (stats.downloading > 0) {
			if (this._progressData.id) {
				clearTimeout(this._progressData.id);
				this._progressData.id = null;
			}

			this._progressData.value = stats.downloading;
		}

		this._lastTime = Date.now();
	}

	/**
	 * Aplikuj transformaci - vezme vstupni pozici, aplikuje trasnformaci a vraci vysledek.
	 *
	 * @param {Object} pos
	 * @param {String} name
	 * @param {Number} x
	 * @param {Number} y
	 * @param {Number} [z]
	 * @return {Object}
	 */
	_applyTransf(pos, name, x, y, z) {
		x = x || 0;
		y = y || 0;

		switch (name) {
			case "pan":
				// factor based on map zoom
				let zoomFactor = pos.z * Math.tan(vts.math.radians(pos.fov * 0.5)) / this._opts.options.panZoomFactor;
				x *= zoomFactor;
				y *= zoomFactor;

				let distance = Math.sqrt(x * x + y * y);
				let azimuth = vts.math.degrees(Math.atan2(x, y)) - pos.yaw;

				pos.setData(this._melownMap.movePositionCoordsTo(pos.toArray(), -azimuth, distance).toArray());
				break;

			case "rotate":
			case "autorotation":
				let yaw = pos.yaw - x;
				let pitch = pos.pitch + y;

				pos.yaw = yaw % 360;
				pos.pitch = pitch;
				break;

			case "zoom":
				pos.z = pos.z * (1 + x);
				break;
		}

		return pos;
	}

	/**
	 * Vlozeni nove transformace k vykresleni. Prida se cas ukonceni,
	 * coz je aktualni ts + cas animace.
	 *
	 * @param {String} name
	 * @param {Number} [x]
	 * @param {Number} [y]
	 * @param {Number} [z]
	 */
	_pushTransfOper(name, x, y, z) {
		let exists = name in this._transform;
		let item = exists ? this._transform[name] : {};

		item.x = x || 0;
		item.y = y || 0;
		item.z = z || 0;
		item.end = Date.now() + this._opts.options.animLength;

		if (!exists) {
			this._transform[name] = item;
		}
	}

	/**
	 * Ukonceni transformace.
	 *
	 * @param {String} name
	 */
	_removeTransfOper(name) {
		if (name in this._transform) {
			this._transform[name].end = 0;
		}
	}

	/**
	 * Nastavit trajektorii pro letecky mod na mape.
	 *
	 * @param {Array} trajectory Pole se seznamem pohledu pro prulet
	 * @param {Object} [opts] Volitelne moznosti
	 */
	_setTrajectory(trajectory, opts) {
		if (!this._melownMap || !trajectory.length) return;

		// disable autorotation
		this.setAutoRotation(false);

		this._trajectory.route = trajectory;
		this._trajectory.start = Date.now();
		this._trajectory.finished = false;
		this._trajectory.samplePeriod = opts && opts.samplePeriod ? opts.samplePeriod : this._conf.options.flySamplePeriod;
		this._trajectory.flyMode = opts && opts.flyMode;

		// interval pro update pozice
		this._runIntervalPosUpdate();

		if (this._trajectory.flyMode) {
			this._makeEvent("fly-start", {
				startPosition: trajectory[0],
				endPosition: trajectory[trajectory.length - 1]
			});
		}
	}

	/**
	 * Nastav copyright.
	 */
	_setCopyright() {
		if (!this._melownMap) return;

		// pole vsech idecek
		let currentCredits = this._melownMap.getCurrentCredits();
		let ids = currentCredits.imagery || [];
		ids = ids.concat(currentCredits.mapdata || []);
		let credits = [];
		let count = 1;

		// detail pro kazde id
		ids.forEach(id => {
			if (id == this._const.SEZNAM_CREDITS_KEY) return;

			credits.push(this._melownMap.getCreditInfo(id).html);
		});

		credits.unshift(this._const.SEZNAM_CREDITS_HTML);

		// na konci vzdycky melown
		credits.push(this._const.MELOWN_CREDITS_HTML);

		const showMore = credits.length > count;
		const allCredits = credits.slice();
		let copyrightParts;

		if (showMore) {
			copyrightParts = credits.splice(0, count);
			copyrightParts[count - 1] += ` ${this._opts.conf.othersLang}`;
		} else {
			copyrightParts = credits;
		}

		const copyrightTxt = copyrightParts.join(" | ");
		const leftoversTxt = credits.join("<br />");

		// otestujeme na posledni hodnotu
		if (leftoversTxt == this._creditsData.lastCont) return;

		this._creditsData.lastCont = leftoversTxt;

		// vypis
		this._dom.copyright.innerHTML = copyrightTxt + (this._opts.conf.inFrame ? ` | ${this._opts.trans.copyrightLicence}` : "");
		// odstranime tagy + veci typu &XYZ;
		this._dom.copyright.title = copyrightTxt.replace(/<[^>]+>|&[^;]+;/g, "");

		// callback - smazeme @copy sign
		allCredits[0] = allCredits[0].replace("&copy; ", "");

		this._opts.map.onCopyright(allCredits);

		if (showMore) {
			let link = this._dom.copyright.querySelector("a[data-others='1']");

			if (link) {
				let popupCover = document.createElement("div");
				popupCover.classList.add("melown-credits-popup-cover");
				let popup = document.createElement("div");
				popup.classList.add("melown-credits-popup");
				let info = document.createElement("p");
				info.innerHTML = leftoversTxt;
				popup.appendChild(info);

				link.addEventListener("click", e => {
					e.preventDefault();
					e.stopPropagation();

					link.focus();
					this._container.appendChild(popup);
					document.body.appendChild(popupCover);
				});
				popupCover.addEventListener("click", e => {
					e.preventDefault();
					e.stopPropagation();

					popup.remove();
					popupCover.remove();
				});
			}
		}
	}

	/**
	 * Zmena pozice mapy.
	 */
	_posChange(pos) {
		if (this._lastPos) {
			// no pos change
			if (this._comparePos(pos, this._lastPos, ["x", "y", "z", "yaw", "pitch"])) return;

			// yaw change
			if (!this._comparePos(pos, this._lastPos, ["yaw"])) {
				// compass
				if (this._compass) {
					this._compass.setYaw(pos.yaw);
				}

				// event
				this._makeEvent("yaw-change", pos.yaw);
			}

			if (this._lastPos.mercZ != pos.mercZ && this._zoomElevationStep) {
				let z = pos.mercZ;

				this._setZoomElevation(z-1);
			}
		}

		this._lastPos = pos;
		this._clearTimeoutPosChange();

		this._posChangeConf.id = setTimeout(() => {
			this._posChangeConf.id = null;
			this._sendPositionChange();
		}, this._posChangeConf.timeout);
	}

	/**
	 * Porovnani dvou pozic, jestli jsou shodne.
	 *
	 * @param  {Object} pos Vstupni pozice
	 * @param  {Object} pos2 Porovnavaci pozice
	 * @param  {Array} attributes Seznam hodnot v objektech, ktere chceme porovnavat
	 * @return {Boolean}
	 */
	_comparePos(pos, pos2, attributes) {
		let SMALL_ZERO = 0.0000001;
		let same = true;

		attributes.every(attr => {
			if (attr in pos && attr in pos2) {
				let diff = pos[attr] - pos2[attr];

				// diff is near the zero
				if (!(diff >= -SMALL_ZERO && diff <= SMALL_ZERO)) {
					same = false;
					return false;
				}
			}

			return true;
		});

		return same;
	}

	/**
	 * Vytvoreni pocatecni pozice.
	 *
	 * @return {Array}
	 */
	_getInitPosition() {
		let posObj = {};

		Object.keys(this._opts.pos).forEach(key => {
			posObj[key] = this._opts.pos[key];
		});

		return new Melown3dPos(posObj);
	}

	/**
	 * Vloz operace pred loadem mapy.
	 *
	 * @param {String} method
	 * @param {Array} args
	 * @return {Boolean} True - bylo vlozeno
	 */
	_preInitPushCall(method, args) {
		// podminka na prvni nacteni
		if (this._firstLoad) {
			this._preInitCalledQueue.push({
				method: method,
				args: Array.prototype.slice.call(args)
			});

			return true;
		}
		else return false;
	}

	/**
	 * Timeout pro zmenu pozice -> event pro update url.
	 */
	_clearTimeoutPosChange() {
		if (this._posChangeConf.id) {
			clearTimeout(this._posChangeConf.id);
			this._posChangeConf.id = null;
		}
	}

	/**
	 * Interval pro vydrazdeni zmeny pozice -> melown negeneruje event map-update pri autorotaci, fly-mode
	 */
	_runIntervalPosUpdate() {
		// jestli uz bezi - znovu nepoustet
		if (this._posUpdateConf.id) return;

		// interval pro update url
		this._posUpdateConf.id = setInterval(() => {
			// zmena pozice pri otaceni
			this._sendPositionChange();
		}, this._posUpdateConf.timeout);
	}

	/**
	 * Zruseni intervalu pro update pozice.
	 */
	_clearIntervalPosUpdate() {
		if (this._posUpdateConf.id) {
			clearTimeout(this._posUpdateConf.id);
			this._posUpdateConf.id = null;
		}
	}

	/**
	 * Poslani signalu o zmeny pozice ve 3d.
	 */
	_sendPositionChange() {
		this._makeEvent("position-change", this.getPosition());
	}

	/**
	 * Pridani vlastni vrstvy.
	 *
	 * @param  {Melown3dLayer} layer
	 */
	_addLayer(layer) {
		if (this._layers.indexOf(layer) == -1) {
			this._layers.push(layer);

			layer.setOwner(this, this._melownCore);
		}
	}

	/**
	 * Odebrani vlastni vrstvy.
	 *
	 * @param  {Melown3dLayer} removeLayer
	 * @return {Boolean} stav odebrani
	 */
	_removeLayer(removeLayer) {
		this._layers.every((layer, ind) => {
			if (layer == removeLayer) {
				layer.destroy();

				this._layers.splice(ind, 1);
				return false;
			}
			else {
				return true;
			}
		});
	}

	/**
	 * Ziskani aktualniho viewportu ve WGS84
	 *
	 * @return {Object} klice lb (left bottom), rt (right top) + osy x a y
	 */
	getViewport() {
		const scene = this._opts.conf.scene;

		if (!scene) {
			return {
				lbx: 0,
				lby: 0,
				rtx: 0,
				rty: 0,
			};
		}

		let pos = this.getPosition();
		let size = this.getSize();
		let height = pos.z * 1.5; // musime vynasobit 1.5 - proste to nesedi :()
		let center = scene.coordsFromWGS84(pos.x, pos.y).toMercator();
		let fovvRad = Math.round(pos.fov / 180 * Math.PI);
		let fovhRad = 2 * Math.atan(Math.tan(fovvRad / 2) * size.x / size.y);
		// pri nakloneni zvetsime sirku a vysku
		let pitchDiff = Math.abs((this._opts.options.maxPitch - pos.pitch) / (this._opts.options.maxPitch - this._opts.options.minPitch));
		// real size [m] - ophoto view
		let halfWidth = Math.tan(fovvRad / 2) * height * (1 + pitchDiff);
		let halfHeight = Math.tan(fovhRad / 2) * height * (1 + pitchDiff);
		let lb = scene.coordsFromMercator(center[0] - halfWidth, center[1] - halfHeight).toWGS84();
		let rt = scene.coordsFromMercator(center[0] + halfWidth, center[1] + halfHeight).toWGS84();

		return {
			lbx: lb[0],
			lby: lb[1],
			rtx: rt[0],
			rty: rt[1]
		};
	}

	/**
	 * Callback pro vlastni render.
	 */
	_customRender() {
		if (!this._melownRender || this._firstLoad || this._layerRedrawLock) return;

		let zoom = this.getPosition().mercZmap;

		if (zoom <= this._const.MAX_HEIGHT_VIEW_MERC) return;

		this._layers.forEach(layer => {
			if (layer.isActive()) {
				layer.render();
			}
		});
	}

	/**
	 * Finalni nastaveni pozice prohlizecky - omezeni pro nezajizdeni pod povrch.
	 *
	 * @param {Array} pos
	 */
	_setPosition(pos) {
		// omezeni na vysku
		pos.z = Math.min(Math.max(pos.z, this._opts.options.minZoom), this._opts.options.maxZoom);
		// pitch
		pos.pitch = Math.max(Math.min(pos.pitch, this._opts.options.minPitch), this._opts.options.maxPitch);

		// zajizdeni pod zem
		this._constrainMapPosition(pos);

		// prvni nacteni a otevrena karta -> nelze menit pohled prohlizecky
		if (!this._firstLoad && this._card.opened) {
			return;
		}

		// omezeni vykresleni volne vrstvy
		if (pos.mercZmap <= this._const.MAX_HEIGHT_VIEW_MERC && !this._freeLayersSave) {
			let view = this._melownMap.getView();
			this._freeLayers.forEach(i => {
				delete view.freeLayers[i.name];
			});
			this._melownMap.setView(view);
			this._freeLayersSave = true;
		}
		else if (pos.mercZmap > this._const.MAX_HEIGHT_VIEW_MERC && this._freeLayersSave) {
			let view = this._melownMap.getView();
			this._freeLayers.forEach(i => {
				view.freeLayers[i.name] = {};
			});
			this._melownMap.setView(view);
			this._freeLayersSave = false;
		}

		// nastavime
		this._melownMap.setPosition(pos.toArray());
	}

	/**
	 * Vygenerovani trajektorie z dane pozici.
	 * Animace: x, y, z (height), yaw, pitch
	 *
	 * @param  {Object} trajectoryPos Objekt s definici souradnic
	 * @param  {Number} [steps = 10] Na kolik dilku rozlozit pohyb
	 * @param  {Boolean} [stopMoveOnBottom = false] Pri dosazeni spodni casti zoomu (zeme) zastavit generovani trajektorie
	 * @return {Array} Pole, kde je definice pozice pro prohlizecku (pro jednotlivy krok)
	 */
	_generateTrajectory(trajectoryPos, steps = 10, stopMoveOnBottom) {
		let oldPos = this.getPosition();
		let newPos = this.getPosition().setData(trajectoryPos);

		let diffX = (newPos.x - oldPos.x) / steps;
		let diffY = (newPos.y - oldPos.y) / steps;
		let diffZ = (newPos.z - oldPos.z) / steps;
		let diffYaw = (newPos.yaw - oldPos.yaw) / steps;
		let diffPitch = (newPos.pitch - oldPos.pitch) / steps;

		let all = [];

		for (let i = 1; i <= steps; i++) {
			let data = {
				x: oldPos.x + diffX * i,
				y: oldPos.y + diffY * i,
				z: oldPos.z + diffZ * i,
				yaw: oldPos.yaw + diffYaw * i,
				pitch: oldPos.pitch + diffPitch * i
			};

			let minZoomTest = data.z < this._opts.options.minZoom;

			// omezeni
			data.z = Math.min(Math.max(data.z, this._opts.options.minZoom), this._opts.options.maxZoom);
			data.pitch = Math.max(Math.min(data.pitch, this._opts.options.minPitch), this._opts.options.maxPitch);

			if (stopMoveOnBottom && minZoomTest) {
				break;
			}
			else {
				// vlozime
				all.push(new Melown3dPos(oldPos).setData(data).toArray());
			}
		}

		return all;
	}

	/**
	 * Od Citationtechu, fakci takto:
	 * - upravuje vysledne pitch natoceni, nehybe se z
	 * - omezime pitch
	 * - zjistime vysku kamery, v rekurzi pak upravujeme, dokud neni ve spravnych hodnotach
	 *
	 * @melown
	 * @param  {Object} pos Vstupni pozice, ktera se ma nastavit prohlizecce
	 * @return {Array} Vystupni pozice, ktera nam nedovoli jit pod teren
	 */
	_constrainMapPosition(pos) {
		let distance = (pos.z * 0.5) / Math.tan(vts.math.radians(pos.fov * 0.5));

		//reduce tilt whe you are far off the planet
		if (pos.viewMode == "obj") {
			let rf = this._melownMap.getReferenceFrame();
			let srs = this._melownMap.getSrsInfo(rf["navigationSrs"]);

			if (srs["a"]) {
				let factor = Math.min(distance / (srs["a"] * 0.5), 1.0);
				let maxTilt = this._opts.options.minPitch + ((this._opts.options.maxPitch) - this._opts.options.minPitch) * factor;
				let minTilt = this._opts.options.maxPitch;
				let pitch = pos.pitch;

				if (pitch > maxTilt) {
					pitch = maxTilt;
				}

				if (pitch < minTilt) {
					pitch = minTilt;
				}

				pos.pitch = pitch;
			}
		}

		// fix souradnic, pokud je mimo rozsah, vratime umele do rozsahu
		if (pos.x > 180) { pos.x = 180; }
		if (pos.x < -180) { pos.x = -180; }
		if (pos.y > 90) { pos.y = 90; }
		if (pos.y < -90) { pos.y = -90; }

		//do not allow camera under terrain
		let camPos = this._melownMap.getPositionCameraCoords(pos.toArray(), "float");
		let cameraConstrainDistance = (this._opts.options.minZoom * 0.5) / Math.tan(vts.math.radians(pos.fov * 0.5));
		cameraConstrainDistance *= 0.5; //divice by 2 to alow 45deg tilt in maximum zoom

		let hmax = Math.max(cameraConstrainDistance, (distance * Math.tan(vts.math.radians(3.0))));
		hmax *= 1.1; // x % nahoru; bufix u nekterych kopcu
		let cameraHeight = camPos[2];

		if (cameraHeight < hmax && cameraHeight != 0) {
			let pitch = pos.pitch;

			let getFinalOrientation = (start, end, level) => {
				let value = (start + end) * 0.5;

				if (level > 20) {
					return value;
				}
				else {
					pitch = value;
					pos.pitch = pitch;

					if (this._melownMap.getPositionCameraCoords(pos.toArray(), "float")[2] < hmax) {
						return getFinalOrientation(start, value, level + 1);
					} else {
						return getFinalOrientation(value, end, level + 1);
					}
				}
			};

			pitch = getFinalOrientation(this._opts.options.maxPitch, Math.min(this._opts.options.minPitch, pitch), 0);
			pos.pitch = pitch;
		}

		return pos;
	}

	/**
	 * Obsluha eventu nad vrstvami.
	 *
	 * @param {String} name Nazev signalu
	 * @param {Event} e
	 * @return {Boolean} Byl nastaven priznak na to, ze se jiz signal nema sirit dal.
	 */
	_layersEvent(name, e) {
		if (this._card.opened) return false;

		let x = e.clientX;
		let y = e.clientY;
		let len = this._layers.length - 1;
		let stopPropagation = false;

		// propagace zindex top -> bottom
		for (let i = len; i >= 0; i--) {
			let layer = this._layers[i];

			if (typeof layer[name] === "function") {
				if (layer[name](x, y)) {
					stopPropagation = true;
					break;
				}
			}
		}

		return stopPropagation;
	}

	/**
	 * Vycisteni timeutu pro zobrazeni karty.
	 */
	_clearCardTimeout() {
		if (this._card.id) {
			clearTimeout(this._card.id);
			this._card.id = null;
		}
	}

	/**
	 * Vycisteni timeoutu pro mouse over nad pozadim karty.
	 */
	_cardCoverMouseOver() {
		this._clearCardTimeout();

		this._card.id = setTimeout(() => {
			this.removeCard();
		}, this._card.timeout);
	}

	/**
	 * Mouse over pres kartu - zastavime propagaci prave kvuli pozadi, ktere kartu odstranuje.
	 * @param  {Event} e
	 */
	_cardElMouseOver(e) {
		this._clearCardTimeout();

		e.stopPropagation();
		e.preventDefault();
	}

	/**
	 * Je hodnota v intervalu?
	 *
	 * @param  {Number}  value Vstupni hodnota
	 * @param  {Number}  min Min. hodnota intervalu
	 * @param  {Number}  max Max. hodnota intervalu
	 * @return {Boolean}
	 */
	_isInInterval(value, min, max) {
		let minValue = Math.min(min, max);
		let maxValue = Math.max(min, max);

		return (value >= minValue && value <= maxValue);
	}

	/**
	 * Byla nactena vsechna data pri zmene.
	 */
	_dataLoadDone() {
		if (this._firstLoad) {
			if (this._spinner) {
				this._spinner.remove();
				this._spinner = null;
			}

			if (this._ophotoLoader) {
				this._ophotoLoader.classList.add("remove");
				this._container.classList.remove("first-load");

				setTimeout(() => {
					this._ophotoLoader.remove();
					this._ophotoLoader = null;
				}, 1250);
			}
			else {
				this._container.classList.remove("first-load");
			}

			this._hide3d = false;

			// compass
			if (this._opts.conf.compass) {
				this._compass = new this._opts.conf.compass(() => {
					this.setToNorth();
				}, this, this._opts.trans);

				// def. angle
				this._compass.setYaw(this.getPosition().yaw);

				this._container.appendChild(this._compass.getContainer());
			}

			// prvni nacteni - hotovo
			this._firstLoad = false;

			// run pre init call stack
			let call = null;
			while (call = this._preInitCalledQueue.shift()) {
				// pokud pridavame vrstvu, zkontrolujeme, zda jsme ji behem nacitani nesmazali
				if (call.method == "addFreeLayer") {
					let remove = false;
					for (let i = 0; i < this._preInitCalledQueue.length; i++) {
						let item = this._preInitCalledQueue[i];
						if (item.method == "removeFreeLayer" && item.args[0] == call.args[0]) {
							this._preInitCalledQueue.splice(i, 1);
							remove = true;
							break;
						}
					}

					if (remove) {
						continue;
					}
				}

				this[call.method].apply(this, call.args);
			}
			this._preInitCalledQueue = null;

			// first full load
			this._makeEvent("map-load");

			// prekreslime
			this.redraw();
		}

		// event
		this._makeEvent("load-done");
	}

	_getHitCoords(x, y, mode = "fix") {
		if (!this._melownMap) return null;

		return this._melownMap.getHitCoords(x, y, mode);
	}

	_elevation(elevationLevel) {
		let view = this._melownMap.getView();

		if (view.options && "superelevation" in view.options && view.options.superelevation == elevationLevel) { return; }

		view.options = { superelevation: elevationLevel };

		this._melownMap.setView(view);

		let elevationText =  this._container.querySelector(".ui-controls button.elevation span");
		if (elevationText) {
			let value = elevationLevel[1][0].toFixed(2).replace(new RegExp("\\.0+$"), "");
			value = value.replace('.', ",").replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + " ");
			elevationText.innerHTML = value;
		}
	}

	_elevationAnimation(newMapset, after) {
		let prom = Promise.resolve();

		if (newMapset == "zakladni" && this._activeElevation) {
			prom = this._elevAnime();
		}
		else if (newMapset != "zakladni" && this._actualMapset == "zakladni" && !after && this._activeElevation) {
			prom = Promise.resolve(true);
		}
		else if (newMapset != "zakladni" && this._actualMapset == "zakladni" && after && this._activeElevation) {
			prom = this._elevAnime(true);
		}
		else {
			prom = Promise.resolve(true);
		}

		return prom;
	}

	_elevAnime(up) {
		let sample = this._opts.options.elevationAnimationSample;
		let pos = this.getPosition();
		let opts = this._getZoomElevation(pos.mercZ-1);
		let elevationStep = [(opts[1][0]-1)/sample, (opts[1][1]-1)/sample];
		let finalState = up ? opts : [opts[0], [1,1]];

		return new Promise((resolve, reject) => {
			this._elev.elevation = up ? [opts[0], [1,1]] : opts;
			this._elev.finished = false;
			let completed = [false, false];
			let iii = 0;

			let interval = setInterval(() => {
				let first = !completed[0] ? this._elev.elevation[1][0] + (up ? elevationStep[0] : -elevationStep[0]) : finalState[1][0];
				let second = !completed[1] ? this._elev.elevation[1][1] + (up ? elevationStep[1] : -elevationStep[1]) : finalState[1][1];

				if (!up) {
					if (first <= 1 && !completed[0]) {
						first = 1;
						completed[0] = true;
					}
					if (second <= 1 && !completed[1]) {
						second = 1;
						completed[1] = true;
					}
				}
				else {
					if (first >= opts[1][0] && !completed[0]) {
						first = opts[1][0];
						completed[0] = true;
					}
					if (second >= opts[1][1] && !completed[1]) {
						second = opts[1][1];
						completed[1] = true;
					}
				}

				if (completed[0] && completed[1]) {
					complete();
				} else {
					this._elev.elevation = [this._elev.elevation[0], [first, second]];
				}

			}, this._opts.options.elevationAnimationLength / (sample+1));

			let complete = () => {
				clearInterval(interval);
				clearTimeout(timeout);

				this._elev.elevation = finalState;
				this._elev.finished = true;

				resolve();
			}

			let timeout = setTimeout(() => {
				complete();
			}, this._opts.options.elevationAnimationLength + this._opts.options.elevationAnimationToleration);
		});
	}

	_countElevationZoomStep(elLevel = this._opts.options.elevationLevel) {
		if (elLevel) {
			let elevLevel = elLevel;
			let keys = Object.keys(elevLevel);

			this._zoomElevationStep = {};

			let countSteps = (start, end) => {
				let startElev = elevLevel[start];
				let endElev = elevLevel[end];
				let steps = end - start;

				this._zoomElevationStep[start] = startElev;
				this._zoomElevationStep[end] = endElev;

				if (steps <= 0) { return; }

				let smallStep = (endElev[1][0] - startElev[1][0]) / steps;
				let bigStep = (endElev[1][1] - startElev[1][1]) / steps;

				for (let i = 1; i < steps; i++) {
					let elev = [startElev[0]];
					elev[1] = [startElev[1][0] + smallStep * i, startElev[1][1] + bigStep * i];
					this._zoomElevationStep[start+i] = elev;
				}
			}

			if (keys.length) {
				let zNumbers = keys.map(z => {
					return parseInt(z);
				});
				zNumbers.sort((a, b) => { return a - b; })

				for (let i = 0; i < zNumbers.length-1; i++) {
					countSteps(zNumbers[i], zNumbers[i+1]);
				}
			} else {
				this._zoomElevationStep = null;
			}
		}
	}

	_getZoomElevation(z) {
		let elevation = [];
		let elevationZoom = Object.keys(this._zoomElevationStep).sort((a, b) => { return a - b; });

		if (this._zoomElevationStep[z]) {
			elevation = this._zoomElevationStep[z]
		}
		else if (z > elevationZoom[elevationZoom.length-1]) {
			elevation = this._zoomElevationStep[elevationZoom[elevationZoom.length-1]];
		}
		else if (z < elevationZoom[0]) {
			elevation = this._zoomElevationStep[elevationZoom[0]];
		}

		return elevation;
	}

	_setZoomElevation(z) {
		let elevation = [];

		if (this._actualMapset == "zakladni" || !this._activeElevation) {
			elevation = [[0, 4000], [1, 1]];
		}
		else {
			elevation = this._getZoomElevation(z);
		}

		if (elevation.length) {
			this._elevation(elevation);
			this._elev.elevation = elevation;
		}
	}

	_disableElevation(disable) {
		if (this._ui) {
			this._ui.disableElevation(disable);
		}
	}
}
